import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, {
  Draggable,
  EventReceiveArg,
  EventResizeDoneArg,
} from "@fullcalendar/interaction";
import {
  DateSelectArg,
  DatesSetArg,
  EventClickArg,
  EventDropArg,
} from "@fullcalendar/core";
import {
  classMgmtService,
  type MonthlyAttendanceResponse,
} from "services/admin/classMgmtService";
import { userService } from "services/admin/userService";
import { venueService } from "services/admin/venueService";
import { hasVietmapServiceKey } from "services/vietmapService";
import { decodeJwt, getCurrentBackendRole } from "utils/auth";
import { useLanguage, type TranslateParams } from "context/LanguageContext";
import type {
  ClassAuditEvent,
  ClassItem,
  ClassSession,
  ClassEnrollment,
  SessionParticipant,
  SessionUpdate,
  UserRole,
  UserListItem,
  VenueItem,
} from "types/domain";
import {
  MdArrowBack,
  MdEdit,
  MdAdd,
  MdCalendarMonth,
  MdPeople,
  MdClose,
  MdSchedule,
  MdHistory,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdWarningAmber,
  MdFileDownload,
} from "react-icons/md";
import Card from "components/card";
import OmniCalendar from "components/calendar/OmniCalendar";
import DateTimeInput from "components/fields/DateTimeInput";
import StatusBadge from "components/ui/StatusBadge";
import {
  getSessionStatusBadgeClass,
  getSessionStatusEventColor,
  getSessionStatusTone,
} from "constants/colors";
import { useVenueAddressSuggestions } from "./hooks/useVenueAddressSuggestions";
import {
  buildSessionTitle,
  parseLocation,
  toFriendlyError,
  toInputDateTime,
} from "./utils/sessionHelpers";
import VenueMapPicker from "./components/VenueMapPicker";
import "./classes-mgmt.css";

type Translate = (key: string, params?: TranslateParams) => string;

const STAFF_ROLES = new Set(["MANAGER", "TRAINER", "TEACHER"]);

const minutesToCalendarDuration = (minutes?: number | null) => {
  const safeMinutes = Math.max(1, minutes ?? 90);
  const hours = String(Math.floor(safeMinutes / 60)).padStart(2, "0");
  const mins = String(safeMinutes % 60).padStart(2, "0");
  return `${hours}:${mins}`;
};

const buildScheduleRange = (
  start: Date,
  durationMinutes?: number | null,
  allDay?: boolean
) => {
  const normalizedStart = new Date(start);
  if (allDay) {
    normalizedStart.setHours(8, 0, 0, 0);
  }
  const safeDuration = Math.max(1, durationMinutes ?? 90);
  const normalizedEnd = new Date(
    normalizedStart.getTime() + safeDuration * 60 * 1000
  );
  return {
    start_time: normalizedStart.toISOString(),
    end_time: normalizedEnd.toISOString(),
  };
};

// CLOSED sessions are shown as finished, same as COMPLETED.
const statusBadgeKey = (status: ClassSession["status"]) => {
  if (status === "ACTIVE") return "enum.sessionStatus.ACTIVE";
  if (status === "COMPLETED" || status === "CLOSED")
    return "enum.sessionStatus.COMPLETED";
  if (status === "CANCELLED") return "enum.sessionStatus.CANCELLED";
  return "enum.sessionStatus.SCHEDULED";
};

type ScheduledStatusFilter = "SCHEDULED" | "ONGOING" | "COMPLETED";

const SCHEDULED_STATUS_FILTER_OPTIONS: Array<{
  key: ScheduledStatusFilter;
  labelKey: string;
}> = [
  { key: "SCHEDULED", labelKey: "enum.sessionStatus.SCHEDULED" },
  { key: "ONGOING", labelKey: "enum.sessionStatus.ACTIVE" },
  { key: "COMPLETED", labelKey: "enum.sessionStatus.COMPLETED" },
];

const getScheduledStatusFilter = (
  status: ClassSession["status"]
): ScheduledStatusFilter => {
  if (status === "ACTIVE") return "ONGOING";
  if (status === "COMPLETED" || status === "CLOSED") return "COMPLETED";
  return "SCHEDULED";
};

const isSessionCalendarLocked = (_status: ClassSession["status"]) => false;

const formatSessionDateLabel = (
  startIso: string | null,
  t: Translate,
  locale: string
) => {
  if (!startIso) return t("classDetail.noDate");
  return new Date(startIso).toLocaleDateString(locale, {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatSessionTimeRange = (
  startIso: string | null,
  endIso: string | null,
  t: Translate,
  locale: string
) => {
  if (!startIso) return t("classDetail.noTimeRange");
  const start = new Date(startIso).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (!endIso) return start;
  const end = new Date(endIso).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${start} -> ${end}`;
};

const formatSessionClock = (iso: string | null, locale: string) => {
  if (!iso) return "--:--";
  return new Date(iso).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatSessionDateCompact = (startIso: string | null, t: Translate) => {
  if (!startIso) return t("classDetail.noDateShort");
  const date = new Date(startIso);
  if (Number.isNaN(date.getTime())) return t("classDetail.noDateShort");
  const weekday = t(`classDetail.weekdayShort.${date.getDay()}`);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${weekday} ${day}/${month}/${year}`;
};

const formatSessionTimeRangeCompact = (
  startIso: string | null,
  endIso: string | null,
  t: Translate,
  locale: string
) => {
  if (!startIso) return t("classDetail.noTimeShort");
  const start = formatSessionClock(startIso, locale);
  if (!endIso) return start;
  const end = formatSessionClock(endIso, locale);
  return `${start} - ${end}`;
};
const isPastDateTime = (value: Date) => value.getTime() < Date.now();

const getHoverPreviewPosition = (
  clientX: number,
  clientY: number,
  previewWidth: number,
  previewHeight: number
) => {
  const margin = 12;
  const offset = 14;

  const canPlaceRight =
    clientX + offset + previewWidth <= window.innerWidth - margin;
  const x = canPlaceRight
    ? clientX + offset
    : Math.max(margin, clientX - previewWidth - offset);

  const canPlaceBelow =
    clientY + offset + previewHeight <= window.innerHeight - margin;
  const y = canPlaceBelow
    ? clientY + offset
    : Math.max(margin, clientY - previewHeight - offset);

  return { x, y };
};

const formatDob = (
  value: string | null | undefined,
  t: Translate,
  locale: string
) => {
  if (!value) return t("classDetail.dobNotUpdated");
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return t("classDetail.dobNotUpdated");
  return parsed.toLocaleDateString(locale);
};

type ParticipantOverlapConflict = {
  userName: string;
  sessionNames: string[];
};

const AUDIT_ACTION_TYPES = new Set([
  "CLASS_CREATED",
  "CLASS_UPDATED",
  "CLASS_DELETED",
  "CLASS_ENROLLMENT_ADDED",
  "CLASS_ENROLLMENT_REMOVED",
  "SESSION_CREATED",
  "SESSION_UPDATED",
  "SESSION_CANCELLED",
  "SESSION_COMPLETED",
  "SESSION_ACTIVATED",
  "SESSION_PARTICIPANTS_SYNCED",
]);

const formatActionLabel = (action: string, t: Translate) =>
  AUDIT_ACTION_TYPES.has(action)
    ? t(`classDetail.auditAction.${action}`)
    : action;

const AUDIT_ROLES = new Set<UserRole>([
  "SYSTEM_ADMIN",
  "MANAGER",
  "TRAINER",
  "TEACHER",
  "STUDENT",
]);

const formatAuditRole = (role: UserRole | null, t: Translate) => {
  if (!role) return t("enum.role.unknown");
  return AUDIT_ROLES.has(role) ? t(`enum.role.${role}`) : role;
};

// "class" has always been labelled the same as "session" here.
const AUDIT_ENTITY_KEY: Record<string, string> = {
  class: "classDetail.auditEntity.class",
  session: "classDetail.auditEntity.session",
  enrollment: "classDetail.auditEntity.enrollment",
  session_participant: "classDetail.auditEntity.session_participant",
};

const formatAuditEntityType = (entityType: string, t: Translate) => {
  const normalized = entityType.trim().toLowerCase();
  const key = AUDIT_ENTITY_KEY[normalized];
  return key ? t(key) : entityType;
};

const CHANGE_FIELDS = new Set([
  "start_time",
  "end_time",
  "duration_minutes",
  "status",
  "title",
  "description",
  "venue_id",
  "location",
  "instructor_id",
  "manager_ids",
  "trainer_ids",
  "student_ids",
  "remaining_sessions",
]);

const formatFieldLabel = (key: string, t: Translate) =>
  CHANGE_FIELDS.has(key) ? t(`classDetail.auditField.${key}`) : key;

// The status field is shared by classes, sessions and enrollments.
const CHANGE_STATUS_KEY: Record<string, string> = {
  OPENING: "enum.classStatus.OPENING",
  ONGOING: "enum.classStatus.ONGOING",
  CLOSED: "enum.classStatus.CLOSED",
  SCHEDULED: "enum.sessionStatus.SCHEDULED",
  ACTIVE: "enum.sessionStatus.ACTIVE",
  COMPLETED: "enum.sessionStatus.COMPLETED",
  CANCELLED: "enum.sessionStatus.CANCELLED",
  RESERVED: "classDetail.auditStatus.RESERVED",
};

const isDateLikeField = (field: string) => {
  const normalized = field.toLowerCase();
  return normalized.includes("time") || normalized.includes("date");
};

const formatAuditFieldValue = (
  field: string,
  value: unknown,
  t: Translate,
  locale: string
): string => {
  if (value === null || value === undefined || value === "")
    return t("classDetail.audit.emptyValue");

  if (typeof value === "string") {
    if (field === "status") {
      const key = CHANGE_STATUS_KEY[value];
      return key ? t(key) : value;
    }

    if (isDateLikeField(field)) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleString(locale);
      }
    }

    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    return value
      .map((item) => formatAuditFieldValue(field, item, t, locale))
      .join(", ");
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getAuditChangedFieldRows = (
  event: ClassAuditEvent,
  t: Translate,
  locale: string
) => {
  const shouldHideField = (field: string) => {
    const normalized = field.trim().toLowerCase();
    return (
      normalized === "created_at" ||
      normalized === "updated_at" ||
      normalized === "id" ||
      normalized.endsWith("_id") ||
      normalized.endsWith("_ids")
    );
  };

  const changedKeys = Object.keys(event.changed_fields ?? {});
  const fallbackKeys = Array.from(
    new Set([
      ...Object.keys(event.before_state ?? {}),
      ...Object.keys(event.after_state ?? {}),
    ])
  );

  const keys = changedKeys.length > 0 ? changedKeys : fallbackKeys;
  const visibleKeys = keys.filter((key) => !shouldHideField(key));

  return visibleKeys.map((key) => {
    const beforeRaw = event.before_state?.[key];
    const changedRaw = event.changed_fields?.[key];
    const afterRaw = event.after_state?.[key] ?? changedRaw;

    return {
      key,
      before: formatAuditFieldValue(key, beforeRaw, t, locale),
      after: formatAuditFieldValue(key, afterRaw, t, locale),
    };
  });
};

const getInitials = (name?: string | null) =>
  (name ?? "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

const UserAvatar = ({
  user,
  size = "md",
}: {
  user?: UserListItem | null;
  size?: "xs" | "sm" | "md";
}) => {
  const sizeClass =
    size === "xs"
      ? "h-8 w-8 text-[11px]"
      : size === "sm"
      ? "h-10 w-10 text-xs"
      : "h-12 w-12 text-sm";

  if (user?.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={user.full_name}
        className={`${sizeClass} rounded-full object-cover`}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} flex items-center justify-center rounded-full bg-brand-50 font-semibold text-brand-600 dark:bg-brand-900/20 dark:text-brand-300`}
    >
      {getInitials(user?.full_name)}
    </div>
  );
};

const ClassDetail = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language, locale } = useLanguage();
  const backendRole = getCurrentBackendRole();
  const token = sessionStorage.getItem("access_token");
  const payload = token ? decodeJwt(token) : null;
  const currentUserId =
    typeof payload?.sub === "string"
      ? payload.sub
      : typeof payload?.user_id === "string"
      ? payload.user_id
      : typeof payload?.id === "string"
      ? payload.id
      : null;
  const canManageClass = backendRole !== "TEACHER";
  const externalSessionsRef = React.useRef<HTMLDivElement | null>(null);

  const [classData, setClassData] = React.useState<ClassItem | null>(null);
  const [sessions, setSessions] = React.useState<ClassSession[]>([]);
  const [enrollments, setEnrollments] = React.useState<ClassEnrollment[]>([]);
  const [students, setStudents] = React.useState<UserListItem[]>([]);
  const [managers, setManagers] = React.useState<UserListItem[]>([]);
  const [trainers, setTrainers] = React.useState<UserListItem[]>([]);
  const [venues, setVenues] = React.useState<VenueItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingStudents, setLoadingStudents] = React.useState(false);
  const [loadingStaff, setLoadingStaff] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingVenues, setLoadingVenues] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingSessionParticipants, setLoadingSessionParticipants] =
    React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [studentSearch, setStudentSearch] = React.useState("");
  const [enrolledStudentSearch, setEnrolledStudentSearch] = React.useState("");
  const [studentSectionCollapsed, setStudentSectionCollapsed] =
    React.useState(false);
  const [studentModalOpen, setStudentModalOpen] = React.useState(false);
  const [addingStudentId, setAddingStudentId] = React.useState<string | null>(
    null
  );
  const [enrollingStudentId, setEnrollingStudentId] = React.useState<
    string | null
  >(null);
  const [remainingSessionsInput, setRemainingSessionsInput] =
    React.useState<string>("");
  const [sessionModalOpen, setSessionModalOpen] = React.useState(false);
  const [calendarCreateConfirm, setCalendarCreateConfirm] = React.useState<{
    start: string;
    end: string;
    duration: number;
  } | null>(null);
  const [calendarActionConfirm, setCalendarActionConfirm] = React.useState<{
    actionLabel: string;
    message: string;
    sessionId: string;
    startTime: string;
    endTime: string;
    durationMinutes?: number;
    managerWarnings?: string[];
  } | null>(null);
  const [savingSession, setSavingSession] = React.useState(false);
  const [creatingVenue, setCreatingVenue] = React.useState(false);
  const [editingSessionId, setEditingSessionId] = React.useState<string | null>(
    null
  );
  const [showCreateVenueForm, setShowCreateVenueForm] = React.useState(false);
  const [sessionManagerIds, setSessionManagerIds] = React.useState<string[]>(
    []
  );
  const [sessionTrainerId, setSessionTrainerId] = React.useState<string>("");
  const [sessionStudentIds, setSessionStudentIds] = React.useState<string[]>(
    []
  );
  const [sessionStudentSearch, setSessionStudentSearch] = React.useState("");
  const [venueForm, setVenueForm] = React.useState({
    name: "",
    address: "",
    description: "",
    latitude: 10.776889,
    longitude: 106.700806,
  });
  const [sessionForm, setSessionForm] = React.useState({
    hasSchedule: true,
    start: "",
    end: "",
    venueId: "",
    durationMinutes: "90",
  });
  const [auditEvents, setAuditEvents] = React.useState<ClassAuditEvent[]>([]);
  const [auditTotal, setAuditTotal] = React.useState(0);
  const [loadingAudit, setLoadingAudit] = React.useState(false);
  const [auditError, setAuditError] = React.useState<string | null>(null);
  const [auditExpanded, setAuditExpanded] = React.useState(false);
  const [expandedAuditEventIds, setExpandedAuditEventIds] = React.useState<
    number[]
  >([]);
  const [auditEmailFilter, setAuditEmailFilter] = React.useState("");
  const [auditActionFilter, setAuditActionFilter] = React.useState("ALL");
  const [auditSessionFilter, setAuditSessionFilter] = React.useState("ALL");
  const [sessionParticipantsBySessionId, setSessionParticipantsBySessionId] =
    React.useState<Record<string, SessionParticipant[]>>({});
  const [hoverPreview, setHoverPreview] = React.useState<{
    session: ClassSession;
    x: number;
    y: number;
    anchorX: number;
    anchorY: number;
  } | null>(null);
  const hoverPreviewTimerRef = React.useRef<number | null>(null);
  const hoverPreviewRef = React.useRef<HTMLDivElement | null>(null);
  const calendarActionRevertRef = React.useRef<(() => void) | null>(null);
  const [scheduledStatusFilter, setScheduledStatusFilter] =
    React.useState<ScheduledStatusFilter | null>(null);
  const [exportMonth, setExportMonth] = React.useState("");
  const [exportPickerOpen, setExportPickerOpen] = React.useState(false);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [attendanceMonth, setAttendanceMonth] = React.useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
  });
  const [attendanceData, setAttendanceData] =
    React.useState<MonthlyAttendanceResponse | null>(null);
  const [loadingAttendance, setLoadingAttendance] = React.useState(false);
  const [attendanceExpanded, setAttendanceExpanded] = React.useState(true);
  const [attendanceTab, setAttendanceTab] = React.useState<
    "students" | "staff"
  >("students");
  const [attendanceSearch, setAttendanceSearch] = React.useState("");
  const [calendarVisibleRange, setCalendarVisibleRange] = React.useState<{
    start: Date;
    end: Date;
  } | null>(null);

  const loadUsersByRole = React.useCallback(
    async (role: "STUDENT" | "TRAINER" | "TEACHER" | "MANAGER") => {
      const limit = 200;
      let skip = 0;
      let allItems: UserListItem[] = [];

      // Backend caps limit at 200, so fetch multiple pages to get full list.
      while (true) {
        const res = await userService.getAll({ role, skip, limit });
        allItems = [...allItems, ...res.items];
        if (res.items.length < limit) break;
        skip += limit;
      }

      return allItems;
    },
    []
  );

  React.useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const [c, s, e, feed] = await Promise.all([
          classMgmtService.getById(id),
          classMgmtService.getSessions(id),
          classMgmtService.getEnrollments(id),
          classMgmtService.getSessionsFeed({ class_id: id, limit: 200 }),
        ]);
        setClassData(c as ClassItem);
        setSessions(s);
        setEnrollments(e);
        // Pre-populate participants cache so hover preview skips individual API calls
        const cache: Record<string, SessionParticipant[]> = {};
        feed.items.forEach((session) => {
          cache[session.id] = session.participants;
        });
        setSessionParticipantsBySessionId(cache);
      } catch (e: any) {
        setError(toFriendlyError(e, "classDetail.error.loadClass"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  React.useEffect(() => {
    const loadStudents = async () => {
      setLoadingStudents(true);
      try {
        const items = await loadUsersByRole("STUDENT");
        setStudents(items);
      } catch {
        setStudents([]);
      } finally {
        setLoadingStudents(false);
      }
    };
    loadStudents();
  }, [loadUsersByRole]);

  React.useEffect(() => {
    const loadStaff = async () => {
      setLoadingStaff(true);
      try {
        const [trainerItems, teacherItems, managerItems] = await Promise.all([
          loadUsersByRole("TRAINER"),
          loadUsersByRole("TEACHER"),
          loadUsersByRole("MANAGER"),
        ]);
        // Class managers: TEACHER + MANAGER
        const mergedManagerMap = new Map<string, UserListItem>();
        [...teacherItems, ...managerItems].forEach((user) =>
          mergedManagerMap.set(user.id, user)
        );
        setManagers(Array.from(mergedManagerMap.values()));
        // Trainers: TRAINER + TEACHER + MANAGER
        const mergedTrainerMap = new Map<string, UserListItem>();
        [...trainerItems, ...teacherItems, ...managerItems].forEach((user) => {
          mergedTrainerMap.set(user.id, user);
        });
        setTrainers(Array.from(mergedTrainerMap.values()));
      } catch {
        setManagers([]);
        setTrainers([]);
      } finally {
        setLoadingStaff(false);
      }
    };
    loadStaff();
  }, [loadUsersByRole]);

  const canViewAuditLogs =
    backendRole === "MANAGER" ||
    (backendRole === "TEACHER" &&
      !!currentUserId &&
      !!classData?.managers?.some((manager) => manager.id === currentUserId));

  React.useEffect(() => {
    if (!id || !canViewAuditLogs) {
      setAuditEvents([]);
      setAuditTotal(0);
      setAuditError(null);
      setExpandedAuditEventIds([]);
      setAuditEmailFilter("");
      setAuditActionFilter("ALL");
      setAuditSessionFilter("ALL");
      return;
    }

    if (!auditExpanded) {
      return;
    }

    const loadAuditEvents = async () => {
      setLoadingAudit(true);
      try {
        const response = await classMgmtService.getAuditEvents(id, {
          skip: 0,
          limit: 20,
        });
        setAuditEvents(response.items);
        setAuditTotal(response.total);
        setAuditError(null);
      } catch (err: any) {
        setAuditEvents([]);
        setAuditTotal(0);
        setAuditError(err.message ?? "classDetail.error.loadAudit");
      } finally {
        setLoadingAudit(false);
      }
    };

    loadAuditEvents();
  }, [id, canViewAuditLogs, auditExpanded]);

  React.useEffect(() => {
    if (!auditExpanded) {
      setExpandedAuditEventIds([]);
    }
  }, [auditExpanded]);

  const auditActionOptions = React.useMemo(() => {
    return Array.from(
      new Set(auditEvents.map((event) => event.action_type))
    ).sort();
  }, [auditEvents]);

  const auditSessionOptions = React.useMemo(() => {
    const sessionEvents = auditEvents.filter((event) => event.session_id);
    const uniqueSessionIds = Array.from(
      new Set(
        sessionEvents
          .map((event) => event.session_id)
          .filter(Boolean) as string[]
      )
    );

    return uniqueSessionIds.map((sessionId) => {
      const title = sessions.find((session) => session.id === sessionId)?.title;
      return {
        value: sessionId,
        label: title ? `${title}` : sessionId,
      };
    });
  }, [auditEvents, sessions]);

  const filteredAuditEvents = React.useMemo(() => {
    const emailKeyword = auditEmailFilter.trim().toLowerCase();

    return auditEvents.filter((event) => {
      const actor = (
        event.actor_email ??
        event.actor_user_id ??
        ""
      ).toLowerCase();
      if (emailKeyword && !actor.includes(emailKeyword)) {
        return false;
      }

      if (
        auditActionFilter !== "ALL" &&
        event.action_type !== auditActionFilter
      ) {
        return false;
      }

      if (auditSessionFilter === "ALL") {
        return true;
      }

      if (auditSessionFilter === "CLASS_ONLY") {
        return event.entity_type.trim().toLowerCase() === "class";
      }

      return event.session_id === auditSessionFilter;
    });
  }, [auditActionFilter, auditEmailFilter, auditEvents, auditSessionFilter]);

  const loadVenues = React.useCallback(async () => {
    try {
      const response = await venueService.getAll({
        center_id: classData?.center_id ?? undefined,
        limit: 200,
      });
      setVenues(response.items);
    } catch {
      setVenues([]);
    }
  }, [classData?.center_id]);

  React.useEffect(() => {
    if (!classData?.center_id) return;
    loadVenues();
  }, [classData?.center_id, loadVenues]);

  const fetchAttendance = React.useCallback(async () => {
    if (!id || !attendanceMonth) return;
    const [y, m] = attendanceMonth.split("-").map(Number);
    setLoadingAttendance(true);
    try {
      const data = await classMgmtService.getMonthlyAttendance(id, y, m);
      setAttendanceData(data);
    } catch {
      setAttendanceData(null);
    } finally {
      setLoadingAttendance(false);
    }
  }, [id, attendanceMonth]);

  React.useEffect(() => {
    if (attendanceExpanded) {
      fetchAttendance();
    }
  }, [attendanceExpanded, fetchAttendance]);

  const handleAddressPreview = React.useCallback(
    (suggestion: { display: string }) => {
      setVenueForm((prev) => ({
        ...prev,
        address: suggestion.display,
      }));
    },
    []
  );

  const handleAddressResolved = React.useCallback(
    (payload: { address: string; latitude: number; longitude: number }) => {
      setVenueForm((prev) => ({
        ...prev,
        address: payload.address,
        latitude: payload.latitude,
        longitude: payload.longitude,
      }));
    },
    []
  );

  const {
    addressSuggestions,
    addressSuggestLoading,
    addressSuggestError,
    handleSelectAddressSuggestion,
  } = useVenueAddressSuggestions({
    enabled: showCreateVenueForm,
    keyword: venueForm.address,
    latitude: venueForm.latitude,
    longitude: venueForm.longitude,
    onSuggestionPreview: handleAddressPreview,
    onSuggestionResolved: handleAddressResolved,
  });

  const venueAddressById = React.useMemo(
    () => new Map(venues.map((venue) => [venue.id, venue.address])),
    [venues]
  );

  const userNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    const pool = [
      ...(classData?.managers ?? []),
      ...managers,
      ...trainers,
      ...students,
      ...(enrollments
        .map((enrollment) => enrollment.student)
        .filter(Boolean) as UserListItem[]),
    ];
    pool.forEach((user) => {
      if (user?.id && user.full_name) {
        map.set(user.id, user.full_name);
      }
    });
    return map;
  }, [classData?.managers, managers, trainers, students, enrollments]);

  const clearHoverPreview = React.useCallback(() => {
    if (hoverPreviewTimerRef.current) {
      window.clearTimeout(hoverPreviewTimerRef.current);
      hoverPreviewTimerRef.current = null;
    }
    setHoverPreview(null);
  }, []);

  const scheduleHoverPreview = React.useCallback(
    (session: ClassSession, event: React.MouseEvent<HTMLButtonElement>) => {
      if (hoverPreviewTimerRef.current) {
        window.clearTimeout(hoverPreviewTimerRef.current);
      }

      const { clientX, clientY } = event;
      hoverPreviewTimerRef.current = window.setTimeout(() => {
        const position = getHoverPreviewPosition(clientX, clientY, 460, 380);
        setHoverPreview({
          session,
          x: position.x,
          y: position.y,
          anchorX: clientX,
          anchorY: clientY,
        });
      }, 1000);
    },
    []
  );

  React.useEffect(() => {
    if (!hoverPreview || !hoverPreviewRef.current) return;

    hoverPreviewRef.current.style.left = `${hoverPreview.x}px`;
    hoverPreviewRef.current.style.top = `${hoverPreview.y}px`;

    const applyMeasuredPosition = () => {
      if (!hoverPreviewRef.current) return;
      const rect = hoverPreviewRef.current.getBoundingClientRect();
      const next = getHoverPreviewPosition(
        hoverPreview.anchorX,
        hoverPreview.anchorY,
        rect.width,
        rect.height
      );

      setHoverPreview((prev) => {
        if (!prev) return prev;
        if (prev.x === next.x && prev.y === next.y) return prev;
        return { ...prev, x: next.x, y: next.y };
      });
    };

    applyMeasuredPosition();
    window.addEventListener("resize", applyMeasuredPosition);
    window.addEventListener("scroll", applyMeasuredPosition, true);

    return () => {
      window.removeEventListener("resize", applyMeasuredPosition);
      window.removeEventListener("scroll", applyMeasuredPosition, true);
    };
  }, [hoverPreview]);

  React.useEffect(
    () => () => {
      if (hoverPreviewTimerRef.current) {
        window.clearTimeout(hoverPreviewTimerRef.current);
      }
    },
    []
  );

  React.useEffect(() => {
    const sessionId = hoverPreview?.session.id;
    if (!sessionId || sessionParticipantsBySessionId[sessionId]) return;

    let cancelled = false;
    const loadPreviewParticipants = async () => {
      try {
        const participants = await classMgmtService.getSessionParticipants(
          sessionId
        );
        if (cancelled) return;
        setSessionParticipantsBySessionId((prev) => ({
          ...prev,
          [sessionId]: participants,
        }));
      } catch {
        if (cancelled) return;
        setSessionParticipantsBySessionId((prev) => ({
          ...prev,
          [sessionId]: [],
        }));
      }
    };

    loadPreviewParticipants();
    return () => {
      cancelled = true;
    };
  }, [hoverPreview?.session.id, sessionParticipantsBySessionId]);

  const handleCreateVenue = async () => {
    if (!venueForm.name.trim() || !venueForm.address.trim()) {
      alert(t("classDetail.error.venueRequired"));
      return;
    }

    setCreatingVenue(true);
    try {
      const createdVenue = await venueService.create({
        center_id: classData?.center_id ?? undefined,
        name: venueForm.name.trim(),
        address: venueForm.address.trim(),
        description: venueForm.description.trim() || undefined,
        location: `${venueForm.latitude.toFixed(
          6
        )},${venueForm.longitude.toFixed(6)}`,
      });
      setVenues((prev) => [createdVenue, ...prev]);
      setSessionForm((prev) => ({ ...prev, venueId: createdVenue.id }));
      setShowCreateVenueForm(false);
    } catch (err: any) {
      alert(err.message ?? t("classDetail.error.createVenue"));
    } finally {
      setCreatingVenue(false);
    }
  };

  const sortedSessions = React.useMemo(
    () =>
      [...sessions].sort((a, b) => {
        const aTime = a.start_time
          ? new Date(a.start_time).getTime()
          : Number.MAX_SAFE_INTEGER;
        const bTime = b.start_time
          ? new Date(b.start_time).getTime()
          : Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
      }),
    [sessions]
  );

  const unscheduledSessions = React.useMemo(
    () =>
      sessions.filter((session) => !session.start_time || !session.end_time),
    [sessions]
  );

  const scheduledSessions = React.useMemo(
    () =>
      sortedSessions.filter(
        (session) => session.start_time && session.end_time
      ),
    [sortedSessions]
  );

  const filteredScheduledSessions = React.useMemo(() => {
    let filtered = scheduledSessions;

    if (calendarVisibleRange) {
      const viewStartMs = calendarVisibleRange.start.getTime();
      const viewEndMs = calendarVisibleRange.end.getTime();

      filtered = filtered.filter((session) => {
        if (!session.start_time || !session.end_time) return false;

        const sessionStartMs = new Date(session.start_time).getTime();
        const sessionEndMs = new Date(session.end_time).getTime();
        if (Number.isNaN(sessionStartMs) || Number.isNaN(sessionEndMs))
          return false;

        // Keep sessions that overlap the currently visible calendar range.
        return sessionStartMs < viewEndMs && sessionEndMs > viewStartMs;
      });
    }

    if (scheduledStatusFilter) {
      filtered = filtered.filter(
        (session) =>
          getScheduledStatusFilter(session.status) === scheduledStatusFilter
      );
    }

    return filtered;
  }, [calendarVisibleRange, scheduledSessions, scheduledStatusFilter]);

  const handleCalendarDatesSet = React.useCallback((arg: DatesSetArg) => {
    setCalendarVisibleRange({
      start: arg.start,
      end: arg.end,
    });
  }, []);

  React.useEffect(() => {
    const missingSessionIds = scheduledSessions
      .map((session) => session.id)
      .filter((sessionId) => !sessionParticipantsBySessionId[sessionId]);

    if (missingSessionIds.length === 0) return;

    let cancelled = false;
    const loadParticipantsForList = async () => {
      let resolved: Array<{
        sessionId: string;
        participants: SessionParticipant[];
      }> = [];
      try {
        const batch = await classMgmtService.getSessionParticipantsBatch(
          missingSessionIds
        );
        resolved = batch.map((item) => ({
          sessionId: item.session_id,
          participants: item.participants,
        }));
      } catch {
        resolved = missingSessionIds.map((sessionId) => ({
          sessionId,
          participants: [] as SessionParticipant[],
        }));
      }

      if (cancelled) return;

      setSessionParticipantsBySessionId((prev) => {
        const next = { ...prev };
        resolved.forEach(({ sessionId, participants }) => {
          if (!next[sessionId]) {
            next[sessionId] = participants;
          }
        });
        return next;
      });
    };

    void loadParticipantsForList();

    return () => {
      cancelled = true;
    };
  }, [scheduledSessions, sessionParticipantsBySessionId]);

  const enrolledStudentIds = React.useMemo(
    () => new Set(enrollments.map((enrollment) => enrollment.student_id)),
    [enrollments]
  );

  const filteredStudents = React.useMemo(() => {
    const keyword = studentSearch.trim().toLowerCase();
    if (!keyword) return students;
    return students.filter((student) =>
      student.full_name.toLowerCase().includes(keyword)
    );
  }, [students, studentSearch]);

  const filteredEnrollments = React.useMemo(() => {
    const keyword = enrolledStudentSearch.trim().toLowerCase();
    if (!keyword) return enrollments;
    return enrollments.filter((enrollment) =>
      (enrollment.student?.full_name ?? "").toLowerCase().includes(keyword)
    );
  }, [enrollments, enrolledStudentSearch]);

  const enrolledStudentsForSession = React.useMemo(() => {
    const map = new Map<string, UserListItem>();
    enrollments.forEach((enrollment) => {
      const fromEnrollment = enrollment.student;
      const fallback = students.find(
        (student) => student.id === enrollment.student_id
      );
      const user = fromEnrollment ?? fallback;
      if (user) {
        map.set(user.id, user);
      }
    });
    return Array.from(map.values());
  }, [enrollments, students]);

  const filteredSessionStudents = React.useMemo(() => {
    const keyword = sessionStudentSearch.trim().toLowerCase();
    if (!keyword) return enrolledStudentsForSession;
    return enrolledStudentsForSession.filter((student) =>
      student.full_name.toLowerCase().includes(keyword)
    );
  }, [enrolledStudentsForSession, sessionStudentSearch]);

  React.useEffect(() => {
    const container = externalSessionsRef.current;
    if (!container || unscheduledSessions.length === 0) return;

    const draggable = new Draggable(container, {
      itemSelector: "[data-session-id]",
      eventData: (eventEl) => ({
        id: eventEl.getAttribute("data-session-id") ?? "",
        title:
          eventEl.getAttribute("data-session-title") ??
          t("classDetail.sessionFallbackTitle"),
        duration: minutesToCalendarDuration(
          Number(eventEl.getAttribute("data-session-duration") ?? "90")
        ),
        extendedProps: {
          sessionId: eventEl.getAttribute("data-session-id") ?? "",
          location: eventEl.getAttribute("data-session-location") ?? "",
          durationMinutes: Number(
            eventEl.getAttribute("data-session-duration") ?? "90"
          ),
        },
      }),
    });

    return () => draggable.destroy();
  }, [unscheduledSessions, t]);

  const calendarEvents = React.useMemo(
    () =>
      filteredScheduledSessions.map((s) => ({
        id: s.id,
        title: s.title,
        start: s.start_time,
        end: s.end_time,
        editable: !isSessionCalendarLocked(s.status),
        startEditable: !isSessionCalendarLocked(s.status),
        durationEditable: !isSessionCalendarLocked(s.status),
        backgroundColor: getSessionStatusEventColor(s.status),
        borderColor: getSessionStatusEventColor(s.status),
        extendedProps: {
          sessionId: s.id,
          durationMinutes: s.duration_minutes,
          location: s.location,
        },
      })),
    [filteredScheduledSessions]
  );

  const applySessionToState = React.useCallback(
    (updatedSession: ClassSession) => {
      setSessions((prev) =>
        prev.map((session) =>
          session.id === updatedSession.id ? updatedSession : session
        )
      );
    },
    []
  );

  const closeSessionModal = () => {
    if (savingSession) return;
    setSessionModalOpen(false);
    setEditingSessionId(null);
    setShowCreateVenueForm(false);
    setSessionManagerIds([]);
    setSessionTrainerId("");
    setSessionStudentIds([]);
    setSessionStudentSearch("");
  };

  const handleCalendarSelect = (info: DateSelectArg) => {
    const start = info.start;
    const end = info.end ?? new Date(start.getTime() + 90 * 60 * 1000);
    if (isPastDateTime(start)) {
      info.view.calendar.unselect();
      alert(t("classDetail.error.pastStart"));
      return;
    }
    const durationMinutes = Math.max(
      1,
      Math.round((end.getTime() - start.getTime()) / 60000) || 90
    );

    info.view.calendar.unselect();

    setCalendarCreateConfirm({
      start: toInputDateTime(start),
      end: toInputDateTime(end),
      duration: durationMinutes,
    });
  };

  const handleConfirmCreateFromCalendar = () => {
    if (!calendarCreateConfirm) return;
    const params = new URLSearchParams({
      start: calendarCreateConfirm.start,
      end: calendarCreateConfirm.end,
      duration: String(calendarCreateConfirm.duration),
    });
    setCalendarCreateConfirm(null);
    navigate(`sessions/new?${params.toString()}`);
  };

  const handleEventClick = (info: EventClickArg) => {
    const targetSession = sessions.find(
      (session) => session.id === info.event.id
    );
    if (targetSession) {
      navigate(`sessions/${targetSession.id}`, {
        state: { from: `${location.pathname}${location.search}` },
      });
    }
  };

  const persistSessionUpdate = React.useCallback(
    async (sessionId: string, payload: SessionUpdate) => {
      const updated = await classMgmtService.updateSession(sessionId, payload);
      applySessionToState(updated);
      return updated;
    },
    [applySessionToState]
  );

  const ensureSessionParticipantsLoaded = React.useCallback(
    async (sessionId: string): Promise<SessionParticipant[]> => {
      const cached = sessionParticipantsBySessionId[sessionId];
      if (cached) {
        return cached;
      }

      const participants = await classMgmtService.getSessionParticipants(
        sessionId
      );
      setSessionParticipantsBySessionId((prev) => ({
        ...prev,
        [sessionId]: participants,
      }));
      return participants;
    },
    [sessionParticipantsBySessionId]
  );

  const checkCalendarParticipantOverlaps = React.useCallback(
    async (
      sessionId: string,
      startTime: string,
      endTime: string
    ): Promise<{
      blockingConflicts: ParticipantOverlapConflict[];
      managerConflicts: ParticipantOverlapConflict[];
    }> => {
      const participants = await ensureSessionParticipantsLoaded(sessionId);

      if (participants.length === 0) {
        return { blockingConflicts: [], managerConflicts: [] };
      }

      const trainerStudentIds = Array.from(
        new Set(
          participants
            .filter(
              (participant) =>
                participant.role === "trainer" || participant.role === "student"
            )
            .map((participant) => participant.user_id)
        )
      );

      const managerIds = Array.from(
        new Set(
          participants
            .filter((participant) => participant.role === "teacher")
            .map((participant) => participant.user_id)
        )
      );

      const allIds = Array.from(new Set([...trainerStudentIds, ...managerIds]));

      if (allIds.length === 0) {
        return { blockingConflicts: [], managerConflicts: [] };
      }

      const overlaps = await classMgmtService.checkSessionParticipantOverlaps({
        user_ids: allIds,
        exclude_session_id: sessionId,
        from_datetime: startTime,
        to_datetime: endTime,
      });

      const overlapsByUser = new Map<string, Set<string>>();
      overlaps.forEach((item) => {
        const sessionName =
          item.session_name || t("classDetail.unknownSessionName");
        const existing = overlapsByUser.get(item.user_id);
        if (existing) {
          existing.add(sessionName);
          return;
        }
        overlapsByUser.set(item.user_id, new Set([sessionName]));
      });

      const buildConflicts = (userIds: string[]) =>
        userIds
          .filter((userId) => overlapsByUser.has(userId))
          .map((userId) => {
            const participantName = participants.find(
              (participant) => participant.user_id === userId
            )?.user?.full_name;
            const userName =
              participantName ?? userNameById.get(userId) ?? userId;
            return {
              userName,
              sessionNames: Array.from(overlapsByUser.get(userId) ?? []),
            };
          });

      return {
        blockingConflicts: buildConflicts(trainerStudentIds),
        managerConflicts: buildConflicts(managerIds),
      };
    },
    [ensureSessionParticipantsLoaded, userNameById, t]
  );

  const handleEventReceive = async (info: EventReceiveArg) => {
    const sessionId =
      (info.event.extendedProps?.sessionId as string | undefined) ??
      info.event.id;
    const draggedSession = sessions.find((session) => session.id === sessionId);
    if (!draggedSession || !info.event.start) {
      info.revert();
      return;
    }

    if (isSessionCalendarLocked(draggedSession.status)) {
      info.revert();
      alert(t("classDetail.error.sessionLocked"));
      return;
    }

    const payload = buildScheduleRange(
      info.event.start,
      draggedSession.duration_minutes,
      info.event.allDay
    );
    let overlapResult;
    try {
      overlapResult = await checkCalendarParticipantOverlaps(
        sessionId,
        payload.start_time,
        payload.end_time
      );
    } catch (err: any) {
      info.revert();
      alert(t(toFriendlyError(err, "classDetail.error.checkOverlap")));
      return;
    }

    if (overlapResult.blockingConflicts.length > 0) {
      info.revert();
      const detail = overlapResult.blockingConflicts
        .map((item) => `- ${item.userName}: ${item.sessionNames.join(", ")}`)
        .join("\n");
      alert(t("classDetail.error.blockingOverlap", { detail }));
      return;
    }

    const managerWarnings = overlapResult.managerConflicts.map(
      (item) => `${item.userName}: ${item.sessionNames.join(", ")}`
    );

    calendarActionRevertRef.current = info.revert;
    setCalendarActionConfirm({
      actionLabel: t("classDetail.confirm.rescheduleTitle"),
      message: t("classDetail.confirm.rescheduleMessage", {
        title: draggedSession.title,
      }),
      sessionId,
      startTime: payload.start_time,
      endTime: payload.end_time,
      managerWarnings,
    });
  };

  const handleEventDrop = async (info: EventDropArg) => {
    if (!info.event.start) {
      info.revert();
      return;
    }

    const currentSession = sessions.find(
      (session) => session.id === info.event.id
    );
    if (!currentSession) {
      info.revert();
      return;
    }

    if (isSessionCalendarLocked(currentSession.status)) {
      info.revert();
      alert(t("classDetail.error.sessionLocked"));
      return;
    }

    const fallbackDuration = currentSession.duration_minutes ?? 90;
    const scheduleRange = buildScheduleRange(
      info.event.start,
      fallbackDuration,
      info.event.allDay
    );
    let overlapResult;
    try {
      overlapResult = await checkCalendarParticipantOverlaps(
        info.event.id,
        scheduleRange.start_time,
        info.event.end ? info.event.end.toISOString() : scheduleRange.end_time
      );
    } catch (err: any) {
      info.revert();
      alert(t(toFriendlyError(err, "classDetail.error.checkOverlap")));
      return;
    }

    if (overlapResult.blockingConflicts.length > 0) {
      info.revert();
      const detail = overlapResult.blockingConflicts
        .map((item) => `- ${item.userName}: ${item.sessionNames.join(", ")}`)
        .join("\n");
      alert(t("classDetail.error.blockingOverlap", { detail }));
      return;
    }

    const managerWarnings = overlapResult.managerConflicts.map(
      (item) => `${item.userName}: ${item.sessionNames.join(", ")}`
    );

    calendarActionRevertRef.current = info.revert;
    setCalendarActionConfirm({
      actionLabel: t("classDetail.confirm.rescheduleTitle"),
      message: t("classDetail.confirm.rescheduleMessage", {
        title: info.event.title,
      }),
      sessionId: info.event.id,
      startTime: scheduleRange.start_time,
      endTime: info.event.end
        ? info.event.end.toISOString()
        : scheduleRange.end_time,
      managerWarnings,
    });
  };

  const handleEventResize = async (info: EventResizeDoneArg) => {
    if (!info.event.start || !info.event.end) {
      info.revert();
      return;
    }

    const currentSession = sessions.find(
      (session) => session.id === info.event.id
    );
    if (!currentSession) {
      info.revert();
      return;
    }

    if (isSessionCalendarLocked(currentSession.status)) {
      info.revert();
      alert(t("classDetail.error.sessionLocked"));
      return;
    }

    const durationMinutes = Math.max(
      1,
      Math.round(
        (info.event.end.getTime() - info.event.start.getTime()) / 60000
      )
    );
    let overlapResult;
    try {
      overlapResult = await checkCalendarParticipantOverlaps(
        info.event.id,
        info.event.start.toISOString(),
        info.event.end.toISOString()
      );
    } catch (err: any) {
      info.revert();
      alert(t(toFriendlyError(err, "classDetail.error.checkOverlap")));
      return;
    }

    if (overlapResult.blockingConflicts.length > 0) {
      info.revert();
      const detail = overlapResult.blockingConflicts
        .map((item) => `- ${item.userName}: ${item.sessionNames.join(", ")}`)
        .join("\n");
      alert(t("classDetail.error.blockingOverlap", { detail }));
      return;
    }

    const managerWarnings = overlapResult.managerConflicts.map(
      (item) => `${item.userName}: ${item.sessionNames.join(", ")}`
    );

    calendarActionRevertRef.current = info.revert;
    setCalendarActionConfirm({
      actionLabel: t("classDetail.confirm.resizeTitle"),
      message: t("classDetail.confirm.resizeMessage", {
        title: info.event.title,
      }),
      sessionId: info.event.id,
      startTime: info.event.start.toISOString(),
      endTime: info.event.end.toISOString(),
      durationMinutes,
      managerWarnings,
    });
  };

  const handleConfirmCalendarAction = async () => {
    if (!calendarActionConfirm) return;

    try {
      await persistSessionUpdate(calendarActionConfirm.sessionId, {
        start_time: calendarActionConfirm.startTime,
        end_time: calendarActionConfirm.endTime,
        duration_minutes: calendarActionConfirm.durationMinutes,
      });
      setCalendarActionConfirm(null);
      calendarActionRevertRef.current = null;
    } catch (err: any) {
      calendarActionRevertRef.current?.();
      calendarActionRevertRef.current = null;
      setCalendarActionConfirm(null);
      alert(t(toFriendlyError(err, "classDetail.error.updateSchedule")));
    }
  };

  const handleCancelCalendarAction = () => {
    calendarActionRevertRef.current?.();
    calendarActionRevertRef.current = null;
    setCalendarActionConfirm(null);
  };

  const handleCreateOrUpdateSession = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    if (!id) return;

    if (!sessionForm.venueId) {
      alert(t("classDetail.error.selectVenue"));
      return;
    }

    if (
      sessionForm.hasSchedule &&
      new Date(sessionForm.end).getTime() <=
        new Date(sessionForm.start).getTime()
    ) {
      alert(t("classDetail.error.endBeforeStart"));
      return;
    }
    if (
      !editingSessionId &&
      sessionForm.hasSchedule &&
      isPastDateTime(new Date(sessionForm.start))
    ) {
      alert(t("classDetail.error.pastStart"));
      return;
    }

    setSavingSession(true);
    try {
      const durationMinutes = Math.max(
        1,
        Number(sessionForm.durationMinutes) || 90
      );
      const payload = {
        venue_id: sessionForm.venueId || null,
        duration_minutes: durationMinutes,
        start_time: sessionForm.hasSchedule
          ? new Date(sessionForm.start).toISOString()
          : null,
        end_time: sessionForm.hasSchedule
          ? new Date(sessionForm.end).toISOString()
          : null,
      };

      let targetSessionId = editingSessionId;

      if (editingSessionId) {
        await persistSessionUpdate(editingSessionId, payload);
      } else {
        if (!payload.venue_id) {
          alert(t("classDetail.error.selectVenue"));
          return;
        }
        const generatedTitle = buildSessionTitle(classData?.name);
        const created = await classMgmtService.createSession({
          title: generatedTitle,
          session_type: "CLASS",
          class_id: id,
          venue_id: payload.venue_id ?? undefined,
          allow_outside_checkin: classData?.allow_outside_checkin,
          start_time: payload.start_time ?? undefined,
          end_time: payload.end_time ?? undefined,
          duration_minutes: durationMinutes,
        });
        setSessions((prev) => [...prev, created]);
        targetSessionId = created.id;
      }

      if (targetSessionId) {
        await classMgmtService.syncSessionParticipants(targetSessionId, {
          teacher_ids: sessionManagerIds,
          teacher_id: sessionManagerIds[0] ?? null,
          trainer_id: sessionTrainerId || null,
          student_ids: sessionStudentIds,
        });
      }
      setSessionModalOpen(false);
    } catch (err: any) {
      alert(err.message ?? t("classDetail.error.saveSession"));
    } finally {
      setSavingSession(false);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm(t("classDetail.confirm.deleteSession"))) return;
    setSavingSession(true);
    try {
      await classMgmtService.deleteSession(sessionId);
      setSessions((prev) => prev.filter((session) => session.id !== sessionId));
      setSessionModalOpen(false);
      setEditingSessionId(null);
    } catch (err: any) {
      alert(err.message ?? t("classDetail.error.deleteSession"));
    } finally {
      setSavingSession(false);
    }
  };

  const confirmEnrollStudent = async (studentId: string) => {
    if (!id) return;
    if (enrolledStudentIds.has(studentId)) return;

    const remaining =
      remainingSessionsInput.trim() === ""
        ? undefined
        : Number(remainingSessionsInput);
    if (
      remainingSessionsInput.trim() !== "" &&
      (Number.isNaN(remaining) || (remaining as number) < 0)
    ) {
      alert(t("classDetail.error.invalidRemaining"));
      return;
    }

    setAddingStudentId(studentId);
    try {
      const createdEnrollment = await classMgmtService.enroll(id, {
        student_id: studentId,
        remaining_sessions: remaining,
      });
      const studentInfo = students.find((student) => student.id === studentId);
      setEnrollments((prev) => [
        ...prev,
        {
          ...createdEnrollment,
          student: studentInfo ?? createdEnrollment.student,
        },
      ]);
      setStudentModalOpen(false);
      setStudentSearch("");
      setEnrollingStudentId(null);
      setRemainingSessionsInput("");
    } catch (err: any) {
      alert(err.message ?? t("classDetail.error.enrollStudent"));
    } finally {
      setAddingStudentId(null);
    }
  };

  if (loading) {
    return (
      <div className="mt-5 flex items-center justify-center py-20">
        <p className="text-sm text-ink-subtle">{t("state.loading")}</p>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="mt-5">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/10 dark:text-red-400">
          {t(error ?? "classDetail.error.classNotFound")}
        </div>
      </div>
    );
  }

  const plannedSessions = sessions.length;
  const assignedManagers = classData.managers ?? [];

  return (
    <div className="mt-5 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/classes-mgmt")}
            className="rounded-lg border border-line p-2 text-ink-muted hover:bg-surface-hover"
          >
            <MdArrowBack className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-ink">
              {classData.name}
            </h2>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <button
              onClick={() => {
                if (!exportMonth) {
                  const now = new Date();
                  setExportMonth(
                    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
                      2,
                      "0"
                    )}`
                  );
                }
                setExportPickerOpen((v) => !v);
              }}
              disabled={exportLoading}
              className="flex items-center gap-1.5 rounded-lg border border-green-500 px-4 py-2 text-sm font-medium text-green-600 hover:bg-green-50 disabled:opacity-50 dark:text-green-400 dark:hover:bg-green-900/10"
            >
              <MdFileDownload className="h-4 w-4" />
              {exportLoading
                ? t("state.loading")
                : t("classDetail.export.button")}
            </button>
            {exportPickerOpen && (
              <div className="absolute right-0 top-full z-20 mt-1 flex items-center gap-2 rounded-lg border border-line bg-surface p-3 shadow-lg">
                <input
                  type="month"
                  value={exportMonth}
                  onChange={(e) => setExportMonth(e.target.value)}
                  className="rounded border border-gray-300 px-2 py-1 text-sm dark:border-navy-500 dark:bg-surface-muted dark:text-white"
                />
                <button
                  onClick={async () => {
                    if (!exportMonth || !id) return;
                    const [y, m] = exportMonth.split("-").map(Number);
                    setExportLoading(true);
                    setExportPickerOpen(false);
                    try {
                      const blob =
                        await classMgmtService.exportMonthlyAttendance(
                          id,
                          y,
                          m
                        );
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `attendance_${y}-${String(m).padStart(
                        2,
                        "0"
                      )}.xlsx`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (err: any) {
                      alert(err.message || t("classDetail.error.export"));
                    } finally {
                      setExportLoading(false);
                    }
                  }}
                  className="rounded-lg bg-green-500 px-3 py-1 text-sm font-medium text-white hover:bg-green-600"
                >
                  {t("classDetail.export.download")}
                </button>
              </div>
            )}
          </div>
          {canManageClass ? (
            <button
              onClick={() => navigate("edit")}
              className="flex items-center gap-1.5 rounded-lg border border-brand-500 px-4 py-2 text-sm font-medium text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/10"
            >
              <MdEdit className="h-4 w-4" />
              {t("action.edit")}
            </button>
          ) : null}
        </div>
      </div>

      {/* Basic Info */}
      <Card extra="p-6">
        <div className="space-y-2.5">
          <p className="text-2xl font-semibold leading-tight text-navy-800 dark:text-white">
            {classData.name}
          </p>
          <p className="text-sm leading-relaxed text-ink">
            {classData.description?.trim() || t("classDetail.noDescription")}
          </p>
        </div>

        {(backendRole === "MANAGER" || backendRole === "TEACHER") && (
          <div className="mt-4 flex items-center gap-3">
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={classData.auto_create_sessions ?? false}
                onChange={async (e) => {
                  const newVal = e.target.checked;
                  try {
                    const updated = await classMgmtService.update(
                      classData.id,
                      {
                        auto_create_sessions: newVal,
                      }
                    );
                    setClassData((prev: any) =>
                      prev ? { ...prev, auto_create_sessions: newVal } : prev
                    );
                  } catch {
                    // revert on failure
                  }
                }}
              />
              <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-500 peer-checked:after:translate-x-full peer-checked:after:border-white dark:bg-surface-muted" />
            </label>
            <span className="text-sm text-ink">
              {t("classDetail.autoCreateSessions")}
            </span>
          </div>
        )}

        <div className="mt-6 border-t border-line pt-5">
          <div className="mb-3 flex items-center gap-2">
            <MdPeople className="h-5 w-5 text-brand-500" />
            <h4 className="text-sm font-semibold text-ink">
              {t("enum.role.TEACHER")}
              <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-normal text-gray-600 dark:text-gray-400">
                {assignedManagers.length}
              </span>
            </h4>
          </div>

          {loadingStaff ? (
            <p className="py-2 text-sm text-ink-muted">
              {t("classDetail.managers.loading")}
            </p>
          ) : assignedManagers.length === 0 ? (
            <p className="py-2 text-sm text-ink-muted">
              {t("classDetail.managers.empty")}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {assignedManagers.map((staff) => (
                <div
                  key={staff.id}
                  className="flex items-start justify-between gap-3 rounded-2xl border border-brand-300 bg-brand-50 p-3 dark:border-brand-400/50 dark:bg-brand-900/10"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <UserAvatar user={staff} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">
                        {staff.full_name}
                      </p>
                      <p className="truncate text-xs text-ink-muted">
                        {staff.email}
                      </p>
                      <p className="mt-1 text-xs text-ink-muted">
                        {t("classDetail.dobLabel", {
                          dob: formatDob(staff.dob, t, locale),
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 flex-col items-end gap-2">
                    <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300">
                      {STAFF_ROLES.has(staff.role)
                        ? t(`enum.role.${staff.role}`)
                        : staff.role}
                    </span>
                    <span className="rounded-full bg-brand-500 px-2 py-0.5 text-xs font-medium text-white">
                      {t("classDetail.managers.inCharge")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5">
        <Card extra="p-6">
          <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-center gap-2">
              <MdPeople className="h-5 w-5 text-brand-500" />
              <h3 className="text-base font-semibold text-ink">
                {t("enum.role.STUDENT")}
                <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-normal text-gray-600 dark:text-gray-400">
                  {enrollments.length}
                </span>
              </h3>
              <button
                type="button"
                onClick={() => setStudentSectionCollapsed((prev) => !prev)}
                aria-label={
                  studentSectionCollapsed
                    ? t("classDetail.students.show")
                    : t("classDetail.students.hide")
                }
                className="rounded-md p-1 text-ink-muted hover:bg-surface-hover hover:text-gray-700 dark:hover:text-white"
              >
                {studentSectionCollapsed ? (
                  <MdKeyboardArrowDown className="h-4 w-4" />
                ) : (
                  <MdKeyboardArrowUp className="h-4 w-4" />
                )}
              </button>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                type="text"
                value={enrolledStudentSearch}
                onChange={(e) => setEnrolledStudentSearch(e.target.value)}
                placeholder={t("classDetail.students.searchPlaceholder")}
                className="w-full rounded-lg border border-line px-3 py-2 text-sm outline-none transition focus:border-brand-500 dark:bg-surface dark:text-white sm:w-72"
              />
              <button
                type="button"
                onClick={() => setStudentModalOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                <MdAdd className="h-4 w-4" />
                {t("classDetail.students.add")}
              </button>
            </div>
          </div>

          {!studentSectionCollapsed && filteredEnrollments.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-subtle">
              {t("classDetail.students.empty")}
            </p>
          ) : !studentSectionCollapsed ? (
            <div className="max-h-[780px] overflow-y-auto pr-1">
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                {filteredEnrollments.map((e) => (
                  <div
                    key={e.id}
                    className="rounded-xl border border-line bg-surface px-2.5 py-2"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <UserAvatar user={e.student} size="xs" />
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold text-ink">
                          {e.student?.full_name ?? e.student_id.slice(0, 8)}
                        </p>
                        <p className="truncate text-[11px] text-ink-muted">
                          {e.student?.email ??
                            t("classDetail.students.noEmail")}
                        </p>
                        <p className="mt-0.5 text-[11px] text-ink-muted">
                          {t("classDetail.dobLabel", {
                            dob: formatDob(e.student?.dob, t, locale),
                          })}
                        </p>
                        <p className="mt-0.5 text-[11px] text-ink-muted">
                          {t("classDetail.students.remaining", {
                            count:
                              (e.remaining_sessions ?? plannedSessions) || "?",
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </Card>
      </div>

      {/* Sessions */}
      <Card extra="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MdCalendarMonth className="h-5 w-5 text-brand-500" />
            <h3 className="text-base font-semibold text-ink">
              {t("classDetail.sessions.title")}
              <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-normal text-gray-600 dark:text-gray-400">
                {t("classDetail.sessions.count", { count: sessions.length })}
              </span>
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("sessions/new")}
              className="text-sm text-brand-500 hover:underline"
            >
              {t("classDetail.sessions.add")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-[360px,minmax(0,1fr)]">
          <div className="h-[920px]">
            <div className="flex h-full flex-col rounded-2xl border border-line bg-white p-4 dark:bg-surface/60">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="text-base font-semibold text-ink">
                    {t("classDetail.sessions.scheduledList")}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {SCHEDULED_STATUS_FILTER_OPTIONS.map((option) => {
                      const active = scheduledStatusFilter === option.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() =>
                            setScheduledStatusFilter((prev) =>
                              prev === option.key ? null : option.key
                            )
                          }
                          className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition ${
                            active
                              ? "border-brand-300 bg-brand-50 text-brand-600 dark:border-brand-500/40 dark:bg-brand-500/20 dark:text-brand-200"
                              : "border-line bg-surface text-gray-600 hover:border-brand-200 hover:text-brand-500 dark:text-gray-300"
                          }`}
                        >
                          {t(option.labelKey)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <span className="rounded-full bg-surface-muted px-2.5 py-1 text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {filteredScheduledSessions.length}
                </span>
              </div>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                {filteredScheduledSessions.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-line px-3 py-5 text-center text-xs text-ink-muted">
                    {t("classDetail.sessions.emptyScheduled")}
                  </div>
                ) : (
                  filteredScheduledSessions.map((session) => {
                    const participants =
                      sessionParticipantsBySessionId[session.id];
                    const sessionTitleParts = session.title
                      .split("/")
                      .map((part) => part.trim())
                      .filter(Boolean);
                    const sessionCode = sessionTitleParts[0] ?? session.title;
                    const sessionTitleSuffix = sessionTitleParts
                      .slice(1)
                      .join(" / ");
                    const hasNoStudents =
                      Array.isArray(participants) &&
                      !participants.some(
                        (participant) => participant.role === "student"
                      );
                    const hasNoManagers =
                      Array.isArray(participants) &&
                      !participants.some(
                        (participant) => participant.role === "teacher"
                      );
                    const showWarning = hasNoStudents || hasNoManagers;

                    return (
                      <button
                        key={session.id}
                        type="button"
                        onClick={() =>
                          navigate(`sessions/${session.id}`, {
                            state: {
                              from: `${location.pathname}${location.search}`,
                            },
                          })
                        }
                        onMouseEnter={(event) =>
                          scheduleHoverPreview(session, event)
                        }
                        onMouseLeave={clearHoverPreview}
                        className="w-full rounded-md border border-line bg-gray-50/60 px-2 py-1.5 text-left transition hover:border-brand-300 hover:bg-white dark:bg-surface-muted/30 dark:hover:bg-surface-hover"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-[11px] text-ink">
                              <span className="font-bold">{sessionCode}</span>
                              {sessionTitleSuffix ? (
                                <span className="font-medium">
                                  {" "}
                                  / {sessionTitleSuffix}
                                </span>
                              ) : null}
                            </p>
                            <p
                              className={`mt-0.5 inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${getSessionStatusBadgeClass(
                                session.status
                              )}`}
                            >
                              {formatSessionTimeRangeCompact(
                                session.start_time,
                                session.end_time,
                                t,
                                locale
                              )}{" "}
                              {formatSessionDateCompact(session.start_time, t)}
                            </p>
                            {(() => {
                              const locationText = session.venue_id
                                ? venueAddressById.get(session.venue_id) ||
                                  session.location
                                : session.location;
                              return locationText ? (
                                <p className="mt-0.5 truncate text-[11px] text-gray-600 dark:text-gray-300">
                                  {locationText}
                                </p>
                              ) : null;
                            })()}
                          </div>
                          <div className="flex items-center gap-1.5">
                            {showWarning ? (
                              <MdWarningAmber
                                className="h-4 w-4 text-amber-500 dark:text-amber-300"
                                title={
                                  hasNoStudents && hasNoManagers
                                    ? t(
                                        "classDetail.sessions.warnNoStudentsNoManagers"
                                      )
                                    : hasNoStudents
                                    ? t("classDetail.sessions.warnNoStudents")
                                    : t("classDetail.sessions.warnNoManagers")
                                }
                              />
                            ) : null}
                            {session.allow_outside_checkin ? (
                              <span
                                className="inline-flex items-center whitespace-nowrap rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300"
                                title={t(
                                  "classDetail.sessions.outsideCheckinTitle"
                                )}
                              >
                                {t("classDetail.sessions.outsideCheckin")}
                              </span>
                            ) : null}
                            <StatusBadge
                              label={t(statusBadgeKey(session.status))}
                              tone={getSessionStatusTone(session.status)}
                              className="whitespace-nowrap px-2 py-0.5 text-[11px]"
                            />
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {hoverPreview
            ? (() => {
                const previewSession = hoverPreview.session;
                const previewParticipants =
                  sessionParticipantsBySessionId[previewSession.id] ?? [];
                const managerNames = previewParticipants
                  .filter((item) => item.role === "teacher")
                  .map(
                    (item) =>
                      userNameById.get(item.user_id) ?? item.user_id.slice(0, 8)
                  );
                const trainerNames = previewParticipants
                  .filter((item) => item.role === "trainer")
                  .map(
                    (item) =>
                      userNameById.get(item.user_id) ?? item.user_id.slice(0, 8)
                  );
                const studentNames = previewParticipants
                  .filter((item) => item.role === "student")
                  .map(
                    (item) =>
                      userNameById.get(item.user_id) ?? item.user_id.slice(0, 8)
                  );

                return (
                  <div
                    ref={hoverPreviewRef}
                    className="classes-hover-preview pointer-events-none fixed z-50 w-[460px] max-w-[calc(100vw-24px)] rounded-2xl border border-line bg-surface shadow-xl"
                  >
                    <div className="flex items-start justify-between gap-3 border-b border-line px-6 py-4">
                      <p className="text-4 font-bold text-ink">
                        {previewSession.title}
                      </p>
                      <StatusBadge
                        label={t(statusBadgeKey(previewSession.status))}
                        tone={getSessionStatusTone(previewSession.status)}
                        className="whitespace-nowrap px-2.5 py-1"
                      />
                    </div>

                    <div className="space-y-3 px-6 py-5 text-sm text-ink">
                      <div className="grid grid-cols-[132px,1fr] items-start gap-2">
                        <p className="font-semibold">
                          {t("classDetail.preview.date")}
                        </p>
                        <p className="font-medium">
                          {formatSessionDateLabel(
                            previewSession.start_time,
                            t,
                            locale
                          )}
                        </p>
                      </div>
                      <div className="grid grid-cols-[132px,1fr] items-start gap-2">
                        <p className="font-semibold">
                          {t("classDetail.preview.timeRange")}
                        </p>
                        <p className="font-medium">
                          {formatSessionTimeRange(
                            previewSession.start_time,
                            previewSession.end_time,
                            t,
                            locale
                          )}
                        </p>
                      </div>
                      <div className="grid grid-cols-[132px,1fr] items-start gap-2">
                        <p className="font-semibold">
                          {t("classDetail.preview.venue")}
                        </p>
                        <p className="font-medium">
                          {previewSession.venue_id
                            ? venueAddressById.get(previewSession.venue_id) ||
                              previewSession.location ||
                              t("classDetail.preview.noAddress")
                            : previewSession.location ||
                              t("classDetail.preview.noAddress")}
                        </p>
                      </div>
                      <div className="grid grid-cols-[132px,1fr] items-start gap-2">
                        <p className="font-semibold">
                          {t("enum.role.TEACHER")}
                        </p>
                        <p className="font-medium">
                          {managerNames.length > 0
                            ? managerNames.join(", ")
                            : t("state.notUpdated")}
                        </p>
                      </div>
                      <div className="grid grid-cols-[132px,1fr] items-start gap-2">
                        <p className="font-semibold">
                          {t("enum.role.TRAINER")}
                        </p>
                        <p className="font-medium">
                          {trainerNames.length > 0
                            ? trainerNames.join(", ")
                            : t("state.notUpdated")}
                        </p>
                      </div>
                      <div className="grid grid-cols-[132px,1fr] items-start gap-2">
                        <p className="font-semibold">
                          {t("enum.role.STUDENT")}
                        </p>
                        <p className="font-medium">
                          {studentNames.length > 0
                            ? studentNames.join(", ")
                            : t("state.notUpdated")}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-line px-6 py-3">
                      <p className="text-xs font-medium text-ink">
                        {t("classDetail.preview.hint")}
                      </p>
                    </div>
                  </div>
                );
              })()
            : null}

          <div className="h-[920px] overflow-hidden rounded-2xl border border-line bg-white p-3 dark:bg-surface/60">
            <div className="mb-3 flex items-center justify-between gap-3 px-2 pt-1">
              <div>
                <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <MdSchedule className="h-4 w-4 text-brand-500" />
                  {t("classDetail.calendar.title")}
                </p>
                <p className="mt-1 text-xs text-ink-muted">
                  {t("classDetail.calendar.hint")}
                </p>
              </div>
            </div>
            <OmniCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              locale={language}
              firstDay={1}
              editable
              droppable
              selectable
              selectMirror
              dayMaxEvents
              nowIndicator
              allDaySlot={false}
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              slotDuration="00:30:00"
              snapDuration="00:30:00"
              slotLabelInterval="01:00:00"
              events={calendarEvents}
              datesSet={handleCalendarDatesSet}
              select={handleCalendarSelect}
              eventClick={handleEventClick}
              eventReceive={handleEventReceive}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              height="100%"
              contentHeight="auto"
              buttonText={{
                today: t("classDetail.calendar.today"),
                month: t("classDetail.calendar.month"),
                week: t("classDetail.calendar.week"),
                day: t("classDetail.calendar.day"),
              }}
            />
          </div>
        </div>
      </Card>

      {/* Monthly Attendance Grid */}
      <Card extra="p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MdCalendarMonth className="h-5 w-5 text-brand-500" />
            <h3 className="text-base font-semibold text-ink">
              {t("classDetail.attendance.title")}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={attendanceMonth}
              onChange={(e) => setAttendanceMonth(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1 text-sm dark:border-navy-500 dark:bg-surface-muted dark:text-white"
            />
            <button
              onClick={() => setAttendanceExpanded((prev) => !prev)}
              aria-label={
                attendanceExpanded
                  ? t("classDetail.attendance.hide")
                  : t("classDetail.attendance.show")
              }
              className="rounded-lg border border-line p-1.5 text-ink-muted hover:bg-surface-hover"
            >
              {attendanceExpanded ? (
                <MdKeyboardArrowUp className="h-4 w-4" />
              ) : (
                <MdKeyboardArrowDown className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
        {attendanceExpanded &&
          (loadingAttendance ? (
            <p className="mt-4 text-sm text-ink-muted">{t("state.loading")}</p>
          ) : attendanceData ? (
            <div className="mt-4">
              <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="w-full lg:max-w-sm">
                  <input
                    type="text"
                    value={attendanceSearch}
                    onChange={(e) => setAttendanceSearch(e.target.value)}
                    placeholder={t("classDetail.attendance.searchPlaceholder")}
                    className="w-full rounded-lg border border-line px-3 py-2 text-sm outline-none transition focus:border-brand-500 dark:bg-surface dark:text-white"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAttendanceTab("students")}
                    className={`rounded-lg px-3 py-1 text-sm font-medium ${
                      attendanceTab === "students"
                        ? "bg-brand-500 text-white"
                        : "bg-surface-muted text-gray-600 hover:bg-gray-200 dark:text-gray-300"
                    }`}
                  >
                    {t("classDetail.attendance.studentsTab", {
                      count: attendanceData.students.members.length,
                    })}
                  </button>
                  <button
                    onClick={() => setAttendanceTab("staff")}
                    className={`rounded-lg px-3 py-1 text-sm font-medium ${
                      attendanceTab === "staff"
                        ? "bg-brand-500 text-white"
                        : "bg-surface-muted text-gray-600 hover:bg-gray-200 dark:text-gray-300"
                    }`}
                  >
                    {t("classDetail.attendance.staffTab", {
                      count: attendanceData.staff.members.length,
                    })}
                  </button>
                </div>
              </div>
              {(() => {
                const grid =
                  attendanceTab === "students"
                    ? attendanceData.students
                    : attendanceData.staff;
                const keyword = attendanceSearch.trim().toLowerCase();
                const filteredMembers = grid.members
                  .map((member, index) => ({ member, index }))
                  .filter(
                    ({ member }) =>
                      !keyword ||
                      member.full_name.toLowerCase().includes(keyword)
                  );
                if (filteredMembers.length === 0) {
                  return (
                    <p className="text-sm text-ink-muted">{t("state.noData")}</p>
                  );
                }
                return (
                  <div className="max-h-[540px] overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-line">
                          <th className="sticky left-0 z-10 bg-surface px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                            {t("classDetail.attendance.colName")}
                          </th>
                          <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                            {t("classDetail.attendance.colDob")}
                          </th>
                          {attendanceTab === "staff" && (
                            <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                              {t("classDetail.attendance.colRole")}
                            </th>
                          )}
                          {grid.sessions.map((s) => (
                            <th
                              key={s.session_id}
                              className="px-2 py-2 text-center font-medium text-gray-600 dark:text-gray-300"
                            >
                              <div className="text-xs leading-tight">
                                {s.date}
                              </div>
                              <div className="text-xs text-ink-subtle">
                                {s.start_time}
                              </div>
                            </th>
                          ))}
                          <th className="px-2 py-2 text-center font-medium text-gray-600 dark:text-gray-300">
                            {t("classDetail.attendance.colTotal")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMembers.map(
                          ({ member, index: memberIndex }) => (
                            <tr
                              key={member.user_id ?? memberIndex}
                              className="border-b border-line hover:bg-surface-hover"
                            >
                              <td className="sticky left-0 z-10 bg-surface px-3 py-2 font-medium text-ink">
                                {member.full_name}
                              </td>
                              <td className="px-2 py-2 text-ink-muted">
                                {member.dob ?? ""}
                              </td>
                              {attendanceTab === "staff" && (
                                <td className="px-2 py-2 text-ink-muted">
                                  {member.role ?? ""}
                                </td>
                              )}
                              {grid.sessions.map((s, sIdx) => (
                                <td
                                  key={s.session_id}
                                  className="px-2 py-2 text-center"
                                >
                                  {s.attendance[memberIndex] ? (
                                    <span className="inline-block h-4 w-4 rounded bg-green-500 text-center text-xs leading-4 text-white">
                                      ✓
                                    </span>
                                  ) : (
                                    <span className="inline-block h-4 w-4 rounded bg-gray-200 dark:bg-surface-hover" />
                                  )}
                                </td>
                              ))}
                              <td className="px-2 py-2 text-center font-semibold text-ink">
                                {grid.totals[memberIndex]}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          ) : (
            <p className="mt-4 text-sm text-ink-muted">{t("state.noData")}</p>
          ))}
      </Card>

      {canViewAuditLogs ? (
        <Card extra="p-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <MdHistory className="h-5 w-5 text-brand-500" />
              <h3 className="text-base font-semibold text-ink">
                {t("classDetail.audit.title")}
                <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-normal text-gray-600 dark:text-gray-400">
                  {filteredAuditEvents.length}/{auditTotal}
                </span>
              </h3>
              <button
                type="button"
                onClick={() => setAuditExpanded((prev) => !prev)}
                aria-label={
                  auditExpanded
                    ? t("classDetail.audit.hide")
                    : t("classDetail.audit.show")
                }
                className="rounded-md p-1 text-ink-muted hover:bg-surface-hover hover:text-gray-700 dark:hover:text-white"
              >
                {auditExpanded ? (
                  <MdKeyboardArrowUp className="h-4 w-4" />
                ) : (
                  <MdKeyboardArrowDown className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {auditExpanded ? (
            loadingAudit ? (
              <p className="pt-4 text-sm text-ink-muted">
                {t("classDetail.audit.loading")}
              </p>
            ) : auditError ? (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/10 dark:text-red-400">
                {t(auditError)}
              </div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-3">
                  <input
                    type="text"
                    value={auditEmailFilter}
                    onChange={(event) =>
                      setAuditEmailFilter(event.target.value)
                    }
                    placeholder={t("classDetail.audit.emailFilter")}
                    className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                  />
                  <select
                    value={auditActionFilter}
                    onChange={(event) =>
                      setAuditActionFilter(event.target.value)
                    }
                    className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                  >
                    <option value="ALL">
                      {t("classDetail.audit.allActions")}
                    </option>
                    {auditActionOptions.map((actionType) => (
                      <option key={actionType} value={actionType}>
                        {formatActionLabel(actionType, t)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={auditSessionFilter}
                    onChange={(event) =>
                      setAuditSessionFilter(event.target.value)
                    }
                    className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                  >
                    <option value="ALL">
                      {t("classDetail.audit.allSessions")}
                    </option>
                    <option value="CLASS_ONLY">
                      {t("classDetail.audit.classOnly")}
                    </option>
                    {auditSessionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {auditEvents.length === 0 ? (
                  <p className="pt-4 text-sm text-ink-muted">
                    {t("classDetail.audit.empty")}
                  </p>
                ) : filteredAuditEvents.length === 0 ? (
                  <p className="pt-4 text-sm text-ink-muted">
                    {t("classDetail.audit.noMatch")}
                  </p>
                ) : (
                  <div className="mt-3 max-h-[420px] overflow-y-auto border-l border-line pl-4 pr-1">
                    {filteredAuditEvents.map((event) =>
                      (() => {
                        const actorDisplay =
                          event.actor_email ??
                          event.actor_user_id ??
                          t("classDetail.audit.systemActor");
                        const normalizedEntityType = event.entity_type
                          .trim()
                          .toLowerCase();
                        const sessionTitle =
                          normalizedEntityType === "session"
                            ? sortedSessions.find(
                                (session) => session.id === event.entity_id
                              )?.title
                            : null;
                        const targetName =
                          normalizedEntityType === "class"
                            ? classData.name
                            : normalizedEntityType === "session"
                            ? sessionTitle ?? event.entity_id
                            : event.entity_id;
                        const affectedEntity = `${formatAuditEntityType(
                          event.entity_type,
                          t
                        )}: ${targetName}`;
                        const isExpanded = expandedAuditEventIds.includes(
                          event.id
                        );
                        const changedFieldRows = getAuditChangedFieldRows(
                          event,
                          t,
                          locale
                        );

                        return (
                          <div
                            key={event.id}
                            className="relative mb-2 rounded-xl border border-line bg-surface px-3 py-2.5 text-sm"
                          >
                            <span className="absolute -left-[22px] top-3 h-2.5 w-2.5 rounded-full bg-brand-500" />
                            <div className="flex items-start justify-between gap-3">
                              <p className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-ink">
                                <span className="text-xs text-ink-muted">
                                  {new Date(event.occurred_at).toLocaleString(
                                    locale
                                  )}
                                </span>
                                <span className="font-semibold">
                                  {actorDisplay}
                                </span>
                                <span className="rounded-full border border-line px-2 py-0.5 text-xs text-ink-muted">
                                  ({formatAuditRole(event.actor_role, t)})
                                </span>
                                <span className="rounded-full border border-line px-2 py-0.5 text-xs text-ink-muted">
                                  ({formatActionLabel(event.action_type, t)})
                                </span>
                                <span
                                  className="max-w-[320px] truncate rounded-full border border-line px-2 py-0.5 text-xs text-ink-muted"
                                  title={affectedEntity}
                                >
                                  ({affectedEntity})
                                </span>
                              </p>
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedAuditEventIds((prev) =>
                                    prev.includes(event.id)
                                      ? prev.filter((item) => item !== event.id)
                                      : [...prev, event.id]
                                  )
                                }
                                className="flex-shrink-0 rounded-lg border border-line px-2 py-1 text-xs font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                              >
                                {isExpanded
                                  ? t("classDetail.audit.collapse")
                                  : t("classDetail.audit.expand")}
                              </button>
                            </div>

                            {isExpanded ? (
                              <div className="mt-2 border-t border-line pt-2">
                                {changedFieldRows.length === 0 ? (
                                  <p className="text-xs text-ink-muted">
                                    {t("classDetail.audit.noDetail")}
                                  </p>
                                ) : (
                                  <div className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                                    {changedFieldRows.map((row) => (
                                      <p key={`${event.id}-${row.key}`}>
                                        <span className="font-medium">
                                          {formatFieldLabel(row.key, t)}:
                                        </span>{" "}
                                        <span className="inline-flex items-center rounded-full border border-line bg-surface-muted px-2 py-0.5 text-[11px] font-medium text-ink-muted">
                                          ({row.before})
                                        </span>{" "}
                                        <span>-&gt;</span>{" "}
                                        <span className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 dark:border-brand-800/60 dark:bg-brand-900/20 dark:text-brand-300">
                                          ({row.after})
                                        </span>
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}
              </>
            )
          ) : null}
        </Card>
      ) : null}

      {sessionModalOpen && (
        <div
          className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeSessionModal}
        >
          <div
            className="w-full max-w-5xl rounded-2xl bg-surface p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-lg font-bold text-ink">
                {editingSessionId
                  ? t("classDetail.sessionForm.editTitle")
                  : t("classDetail.sessionForm.createTitle")}
              </h4>
              <button
                onClick={closeSessionModal}
                className="rounded-lg p-1 text-ink-subtle hover:bg-surface-hover"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateOrUpdateSession} className="space-y-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-ink-muted">
                    {t("classDetail.sessionForm.duration")}
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={sessionForm.durationMinutes}
                    onChange={(e) =>
                      setSessionForm((prev) => ({
                        ...prev,
                        durationMinutes: e.target.value,
                      }))
                    }
                    className="bg-transparent w-full rounded-lg border border-line px-4 py-2.5 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 rounded-xl border border-line px-3 py-2.5 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={sessionForm.hasSchedule}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSessionForm((prev) => {
                          const next = { ...prev, hasSchedule: checked };
                          if (checked && (!prev.start || !prev.end)) {
                            const start = new Date();
                            const end = new Date(
                              start.getTime() +
                                Math.max(
                                  1,
                                  Number(prev.durationMinutes) || 90
                                ) *
                                  60 *
                                  1000
                            );
                            next.start = toInputDateTime(start);
                            next.end = toInputDateTime(end);
                          }
                          return next;
                        });
                      }}
                    />
                    {t("classDetail.sessionForm.hasSchedule")}
                  </label>
                </div>
              </div>

              {sessionForm.hasSchedule ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DateTimeInput
                    id="session-start"
                    label={t("classDetail.sessionForm.start")}
                    required
                    value={sessionForm.start}
                    onChange={(value) =>
                      setSessionForm((prev) => ({ ...prev, start: value }))
                    }
                  />
                  <DateTimeInput
                    id="session-end"
                    label={t("classDetail.sessionForm.end")}
                    required
                    value={sessionForm.end}
                    onChange={(value) =>
                      setSessionForm((prev) => ({ ...prev, end: value }))
                    }
                  />
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-line px-4 py-3 text-sm text-ink-muted">
                  {t("classDetail.sessionForm.unscheduledHint")}
                </div>
              )}

              <div className="rounded-2xl border border-line p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <label className="text-sm font-semibold text-ink">
                    {t("classDetail.sessionForm.venue")}
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCreateVenueForm((prev) => !prev)}
                    className="text-xs font-medium text-brand-500 hover:underline"
                  >
                    {showCreateVenueForm
                      ? t("classDetail.sessionForm.hideVenueForm")
                      : t("classDetail.sessionForm.showVenueForm")}
                  </button>
                </div>

                <select
                  value={sessionForm.venueId}
                  onChange={(e) => {
                    const venueId = e.target.value;
                    setSessionForm((prev) => ({ ...prev, venueId }));
                    const selectedVenue = venues.find(
                      (venue) => venue.id === venueId
                    );
                    const parsedLocation = parseLocation(
                      selectedVenue?.location
                    );
                    if (parsedLocation) {
                      setVenueForm((prev) => ({
                        ...prev,
                        latitude: parsedLocation.lat,
                        longitude: parsedLocation.lng,
                      }));
                    }
                  }}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                >
                  <option value="">
                    {t("classDetail.sessionForm.selectVenue")}
                  </option>
                  {venues.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                      {venue.address ? ` - ${venue.address}` : ""}
                    </option>
                  ))}
                </select>

                {loadingVenues && (
                  <p className="mt-2 text-xs text-ink-muted">
                    {t("classDetail.sessionForm.loadingVenues")}
                  </p>
                )}

                {sessionForm.venueId && (
                  <p className="mt-2 text-xs text-ink-muted">
                    {(() => {
                      const selectedVenue = venues.find(
                        (venue) => venue.id === sessionForm.venueId
                      );
                      if (!selectedVenue) return "";
                      const address =
                        selectedVenue.address ??
                        t("classDetail.sessionForm.noAddress");
                      return selectedVenue.location
                        ? t("classDetail.sessionForm.venueAddressWithCoords", {
                            address,
                            coords: selectedVenue.location,
                          })
                        : t("classDetail.sessionForm.venueAddress", {
                            address,
                          });
                    })()}
                  </p>
                )}

                {showCreateVenueForm && (
                  <div className="mt-4 space-y-3 rounded-xl border border-line bg-gray-50/60 p-4 dark:bg-surface-muted/30">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                          {t("classDetail.sessionForm.venueName")}
                        </label>
                        <input
                          type="text"
                          value={venueForm.name}
                          onChange={(e) =>
                            setVenueForm((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          placeholder={t(
                            "classDetail.sessionForm.venueNamePlaceholder"
                          )}
                          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                          {t("classDetail.sessionForm.address")}
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={venueForm.address}
                            onChange={(e) =>
                              setVenueForm((prev) => ({
                                ...prev,
                                address: e.target.value,
                              }))
                            }
                            placeholder={t(
                              "classDetail.sessionForm.addressPlaceholder"
                            )}
                            className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
                          />

                          {showCreateVenueForm &&
                          venueForm.address.trim().length >= 2 ? (
                            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line bg-surface shadow-lg">
                              {addressSuggestLoading ? (
                                <p className="px-3 py-2 text-xs text-ink-muted">
                                  {t(
                                    "classDetail.sessionForm.searchingAddress"
                                  )}
                                </p>
                              ) : addressSuggestError ? (
                                <p className="px-3 py-2 text-xs text-red-500">
                                  {addressSuggestError}
                                </p>
                              ) : addressSuggestions.length > 0 ? (
                                <div className="max-h-56 overflow-y-auto py-1">
                                  {addressSuggestions.map((suggestion) => (
                                    <button
                                      key={suggestion.id}
                                      type="button"
                                      onClick={() => {
                                        void handleSelectAddressSuggestion(
                                          suggestion
                                        );
                                      }}
                                      className="block w-full px-3 py-2 text-left text-xs text-ink hover:bg-surface-hover"
                                    >
                                      {suggestion.display}
                                    </button>
                                  ))}
                                </div>
                              ) : hasVietmapServiceKey ? (
                                <p className="px-3 py-2 text-xs text-ink-muted">
                                  {t(
                                    "classDetail.sessionForm.noAddressSuggestion"
                                  )}
                                </p>
                              ) : (
                                <p className="px-3 py-2 text-xs text-amber-600 dark:text-amber-300">
                                  {t(
                                    "classDetail.sessionForm.missingVietmapKey"
                                  )}
                                </p>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t("classDetail.sessionForm.venueDescription")}
                      </label>
                      <input
                        type="text"
                        value={venueForm.description}
                        onChange={(e) =>
                          setVenueForm((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        placeholder={t(
                          "classDetail.sessionForm.venueDescriptionPlaceholder"
                        )}
                        className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                          {t("classDetail.sessionForm.latitude")}
                        </label>
                        <input
                          type="number"
                          step="0.000001"
                          value={venueForm.latitude}
                          onChange={(e) =>
                            setVenueForm((prev) => ({
                              ...prev,
                              latitude: Number(e.target.value) || prev.latitude,
                            }))
                          }
                          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                          {t("classDetail.sessionForm.longitude")}
                        </label>
                        <input
                          type="number"
                          step="0.000001"
                          value={venueForm.longitude}
                          onChange={(e) =>
                            setVenueForm((prev) => ({
                              ...prev,
                              longitude:
                                Number(e.target.value) || prev.longitude,
                            }))
                          }
                          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                        />
                      </div>
                    </div>

                    <VenueMapPicker
                      latitude={venueForm.latitude}
                      longitude={venueForm.longitude}
                      onPick={(lat, lng) =>
                        setVenueForm((prev) => ({
                          ...prev,
                          latitude: lat,
                          longitude: lng,
                        }))
                      }
                    />

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleCreateVenue}
                        disabled={creatingVenue}
                        className="rounded-lg bg-brand-500 px-4 py-2 text-xs font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {creatingVenue
                          ? t("classDetail.sessionForm.creatingVenue")
                          : t("classDetail.sessionForm.createVenue")}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-line bg-gray-50/70 p-4 dark:bg-surface-muted/20">
                <div className="mb-3 flex items-center justify-between">
                  <h5 className="text-sm font-semibold text-ink">
                    {t("classDetail.sessionForm.participants")}
                  </h5>
                  <span className="rounded-full bg-surface px-2.5 py-1 text-xs font-semibold text-gray-600 dark:text-gray-300">
                    {t("classDetail.sessionForm.studentCount", {
                      count: sessionStudentIds.length,
                    })}
                  </span>
                </div>

                {loadingSessionParticipants ? (
                  <p className="py-2 text-sm text-ink-muted">
                    {t("classDetail.sessionForm.loadingParticipants")}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t("enum.role.TEACHER")}
                      </label>
                      <select
                        multiple
                        value={sessionManagerIds}
                        onChange={(e) => {
                          const values = Array.from(e.target.selectedOptions)
                            .map((option) => option.value)
                            .filter(Boolean);
                          setSessionManagerIds(values);
                        }}
                        className="min-h-[110px] w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                      >
                        {managers.map((manager) => (
                          <option key={manager.id} value={manager.id}>
                            {manager.full_name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-ink-muted">
                        {t("classDetail.sessionForm.multiSelectHint")}
                      </p>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t("enum.role.TRAINER")}
                      </label>
                      <select
                        value={sessionTrainerId}
                        onChange={(e) => setSessionTrainerId(e.target.value)}
                        className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                      >
                        <option value="">
                          {t("classDetail.sessionForm.selectTrainer")}
                        </option>
                        {trainers.map((trainer) => (
                          <option key={trainer.id} value={trainer.id}>
                            {trainer.full_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t("classDetail.sessionForm.searchStudents")}
                      </label>
                      <input
                        type="text"
                        value={sessionStudentSearch}
                        onChange={(e) =>
                          setSessionStudentSearch(e.target.value)
                        }
                        placeholder={t(
                          "classDetail.sessionForm.studentNamePlaceholder"
                        )}
                        className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
                      />
                    </div>
                  </div>
                )}

                {!loadingSessionParticipants && (
                  <div className="mt-3 max-h-52 space-y-2 overflow-y-auto pr-1">
                    {filteredSessionStudents.length === 0 ? (
                      <p className="rounded-xl border border-dashed border-line px-3 py-5 text-center text-xs text-ink-muted">
                        {t("classDetail.sessionForm.noClassStudents")}
                      </p>
                    ) : (
                      filteredSessionStudents.map((student) => {
                        const checked = sessionStudentIds.includes(student.id);
                        return (
                          <label
                            key={student.id}
                            className="flex cursor-pointer items-center justify-between rounded-xl border border-line bg-white px-3 py-2 dark:bg-surface-muted/50"
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setSessionStudentIds((prev) => {
                                    if (isChecked) return [...prev, student.id];
                                    return prev.filter(
                                      (idValue) => idValue !== student.id
                                    );
                                  });
                                }}
                              />
                              <span className="text-sm font-medium text-ink">
                                {student.full_name}
                              </span>
                            </div>
                            <span className="text-xs text-ink-muted">
                              {student.email}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-1">
                {editingSessionId && (
                  <button
                    type="button"
                    onClick={() => handleDeleteSession(editingSessionId)}
                    disabled={savingSession}
                    className="mr-auto rounded-lg px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-50 disabled:opacity-60 dark:hover:bg-red-900/10"
                  >
                    {t("classDetail.sessionForm.delete")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeSessionModal}
                  className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink hover:bg-surface-hover"
                >
                  {t("action.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={savingSession}
                  className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {savingSession
                    ? t("action.saving")
                    : editingSessionId
                    ? t("classDetail.sessionForm.saveChanges")
                    : t("classDetail.sessionForm.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {calendarCreateConfirm && (
        <div
          className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setCalendarCreateConfirm(null)}
        >
          <div
            className="w-full max-w-xl overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-line px-6 py-4">
              <h3 className="text-lg font-bold text-ink">
                {t("classDetail.confirm.createTitle")}
              </h3>
            </div>

            <div className="space-y-3 px-6 py-5 text-sm text-ink">
              <p className="text-base font-medium text-ink">
                {t("classDetail.confirm.createMessage")}
              </p>
              <div className="rounded-xl bg-surface-muted p-3">
                <p className="font-semibold">
                  {formatSessionDateLabel(
                    new Date(calendarCreateConfirm.start).toISOString(),
                    t,
                    locale
                  )}
                </p>
                <p className="mt-1 font-medium">
                  {formatSessionTimeRange(
                    new Date(calendarCreateConfirm.start).toISOString(),
                    new Date(calendarCreateConfirm.end).toISOString(),
                    t,
                    locale
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-line px-6 py-4">
              <button
                type="button"
                onClick={() => setCalendarCreateConfirm(null)}
                className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink hover:bg-surface-hover"
              >
                {t("action.cancel")}
              </button>
              <button
                type="button"
                onClick={handleConfirmCreateFromCalendar}
                className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                {t("action.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {calendarActionConfirm && (
        <div
          className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={handleCancelCalendarAction}
        >
          <div
            className="w-full max-w-xl overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-line px-6 py-4">
              <h3 className="text-lg font-bold text-ink">
                {calendarActionConfirm.actionLabel}
              </h3>
            </div>

            <div className="space-y-3 px-6 py-5 text-sm text-ink">
              <p className="text-base font-medium text-ink">
                {calendarActionConfirm.message}
              </p>
              <div className="rounded-xl bg-surface-muted p-3">
                <p className="font-semibold">
                  {formatSessionDateLabel(
                    calendarActionConfirm.startTime,
                    t,
                    locale
                  )}
                </p>
                <p className="mt-1 font-medium">
                  {formatSessionTimeRange(
                    calendarActionConfirm.startTime,
                    calendarActionConfirm.endTime,
                    t,
                    locale
                  )}
                </p>
              </div>
              {calendarActionConfirm.managerWarnings &&
              calendarActionConfirm.managerWarnings.length > 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30">
                  <p className="font-semibold">
                    {t("classDetail.confirm.managerOverlapTitle")}
                  </p>
                  <p className="mt-1 text-xs">
                    {t("classDetail.confirm.managerOverlapHint")}
                  </p>
                  <p className="mt-2 whitespace-pre-line text-xs">
                    {calendarActionConfirm.managerWarnings
                      .map((item) => `- ${item}`)
                      .join("\n")}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-line px-6 py-4">
              <button
                type="button"
                onClick={handleCancelCalendarAction}
                className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink hover:bg-surface-hover"
              >
                {t("action.cancel")}
              </button>
              <button
                type="button"
                onClick={handleConfirmCalendarAction}
                className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                {t("action.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {studentModalOpen && (
        <div
          className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => {
            if (!addingStudentId) {
              setStudentModalOpen(false);
            }
          }}
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-surface p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-bold text-ink">
                  {t("classDetail.enroll.title")}
                </h4>
                <p className="mt-1 text-sm text-ink-muted">
                  {t("classDetail.enroll.subtitle")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStudentModalOpen(false)}
                disabled={Boolean(addingStudentId)}
                className="rounded-lg p-1 text-ink-subtle hover:bg-surface-hover disabled:opacity-60"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                {t("classDetail.enroll.searchLabel")}
              </label>
              <input
                type="text"
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                placeholder={t(
                  "classDetail.sessionForm.studentNamePlaceholder"
                )}
                className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
              />
            </div>

            {loadingStudents ? (
              <p className="py-8 text-center text-sm text-ink-muted">
                {t("classDetail.enroll.loading")}
              </p>
            ) : filteredStudents.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-muted">
                {t("classDetail.enroll.noMatch")}
              </p>
            ) : (
              <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                {filteredStudents.map((student) => {
                  const alreadyEnrolled = enrolledStudentIds.has(student.id);
                  const isAdding = addingStudentId === student.id;
                  const isEnrolling = enrollingStudentId === student.id;

                  return (
                    <div
                      key={student.id}
                      className="rounded-xl border border-line bg-white dark:bg-surface-muted/60"
                    >
                      <div className="flex items-center justify-between gap-3 px-3 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <UserAvatar user={student} />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-ink">
                              {student.full_name}
                            </p>
                            <p className="truncate text-xs text-ink-muted">
                              {student.email}
                            </p>
                            <p className="mt-1 text-xs text-ink-muted">
                              {t("classDetail.dobLabel", {
                                dob: formatDob(student.dob, t, locale),
                              })}
                            </p>
                          </div>
                        </div>
                        {alreadyEnrolled ? (
                          <span className="ml-3 flex-shrink-0 rounded-full bg-gray-200 px-3 py-1.5 text-xs font-semibold text-ink-muted dark:bg-surface-hover">
                            {t("classDetail.enroll.alreadyEnrolled")}
                          </span>
                        ) : isEnrolling ? null : (
                          <button
                            type="button"
                            onClick={() => {
                              setEnrollingStudentId(student.id);
                              setRemainingSessionsInput("");
                            }}
                            className="ml-3 flex-shrink-0 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
                          >
                            {t("action.add")}
                          </button>
                        )}
                      </div>

                      {isEnrolling && (
                        <div className="flex items-center gap-3 border-t border-line px-3 pb-3 pt-2.5">
                          <div className="flex-1">
                            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                              {t("classDetail.enroll.remainingSessions")}{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={remainingSessionsInput}
                              onChange={(e) =>
                                setRemainingSessionsInput(e.target.value)
                              }
                              placeholder={t(
                                "classDetail.enroll.remainingPlaceholder"
                              )}
                              className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
                            />
                          </div>
                          <div className="mt-5 flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setEnrollingStudentId(null);
                                setRemainingSessionsInput("");
                              }}
                              disabled={isAdding}
                              className="rounded-lg border border-line px-3 py-2 text-xs font-medium text-gray-600 hover:bg-surface-hover disabled:opacity-60 dark:text-gray-400"
                            >
                              {t("action.cancel")}
                            </button>
                            <button
                              type="button"
                              onClick={() => confirmEnrollStudent(student.id)}
                              disabled={
                                isAdding || remainingSessionsInput.trim() === ""
                              }
                              className="rounded-lg bg-brand-500 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {isAdding
                                ? t("classDetail.enroll.adding")
                                : t("action.confirm")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassDetail;
